import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';

import { AdvertLocationDto, EstateAgentDto, EstateAgentPersonalInformationDto, EstateAgentStatusDto, GenericResultDto, MetroDto, PropertySectorDto, UserDto } from '../../../../../app/services/property-matrixV2/models';
import { EstateAgentRegistrationStateService } from '../../../../../app/shared/services/registration/estate-agent-registration-state/estate-agent-registration-state.service';
import { LookupService, UserManagementService } from '../../../../../app/services/property-matrixV2/services';
import { NotificationService } from '../../../../shared/services/notification-service/notification.service';

@Component({
  selector: 'app-estate-agent-personal-information',
  templateUrl: './estate-agent-personal-information.component.html',
  styleUrls: ['./estate-agent-personal-information.component.scss', '../../../../../css/2-modules/_forms.scss']
})
export class EstateAgentPersonalInformationComponent implements OnInit {

  @Output() pageNumberEvent = new EventEmitter<number>();
  @Output() formSubmittedEvent = new EventEmitter<void>();

  estateAgentForm: FormGroup;
  propertySectors: PropertySectorDto[] = [];
  estateAgentStatuses: EstateAgentStatusDto[] = [];

  selectedButton: string | null = null;
  isNotPrincipal: boolean = false;
  isPrincipal: boolean = false;
  loading: boolean = false;
  metros: AdvertLocationDto[] = [];
  metroList: MetroDto[] = [];
  previoususerCreated: UserDto;
  userId: string | undefined;

  constructor(
    private route: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _lookupService: LookupService,
    private notificationService: NotificationService,
    private userManagementService: UserManagementService,
    private _estateAgentRegistrationStateService: EstateAgentRegistrationStateService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isNotPrincipal = params['isPrincipal'] === 'false';
      this.isPrincipal = params['isPrincipal'] === 'true';
      this.userId = params['userID'] ?? undefined;
    });

    this.estateAgentForm = this._formBuilder.group({
      status: new FormControl(null, [Validators.required]),
      sectors: new FormArray([], this.minSelectedCheckboxes(1)),
      otherSectors: new FormControl(null),
      metro: new FormControl(null, [Validators.required]),
      yearsInOperation: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(100)])
    });

    this.fetchMetros();
    this.fetchEstateAgentStatuses();
    this.fetchPropertySectors();
    this.getMetroName();
    if (this.userId != undefined) {
      this.fetchCreatedUserWithEstateUserId();
    }
  }

  fetchEstateAgentStatuses() {
    this._lookupService.apiV1LookupGetEstateAgentStatusesGet().subscribe({
      next: (response: EstateAgentStatusDto[]) => {
        if (response && response.length > 0) {
          this.estateAgentStatuses = response.sort((a, b) => a.value.localeCompare(b.value));
        } else {
          this.notificationService.showErrorMessage("Error", "Could not get Agent statuses.");
        }
      }
    });
  }

  fetchPropertySectors() {
    this._lookupService.apiV1LookupGetPropertySectorsGet().subscribe({
      next: (response: PropertySectorDto[]) => {
        if (response && response.length > 0) {
          this.propertySectors = response.sort((a, b) => a.intValue - b.intValue);
          this.addSectorCheckboxes();
        } else {
          this.notificationService.showErrorMessage("Error", "Could not load property sector data.");
        }
      }
    });
  }

  fetchMetros(): void {
    this.loading = true;
    this._lookupService.apiV1LookupGetAllMetroItemsGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get metro data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: MetroDto[] | void) => {
        if (result && result.length > 0) {
          this.metroList = result.sort((a, b) =>
            a.description.localeCompare(b.description)
          );
        } else {
          this.notificationService.showErrorMessage("Error", "Could not load metro data.");
        }
      });
  }

  fetchCreatedUserWithEstateUserId(): void {
    if (this.userId) {
      this.loading = true;
      this.userManagementService.apiV1UserManagementGetCurrentUserByIdGet({ id: this.userId })
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not get user by Id."))
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((result: UserDto | void) => {
          if (result) {
            this.previoususerCreated = result;
          } else {
            this.notificationService.showErrorMessage("Error", "Could not get user by Id.");
          }
        });
    } else {
      this.notificationService.showErrorMessage("Error", "Could not get user by Id.");
    }
  }

  getMetroName() {
    this._lookupService.apiV1LookupGetAdvertLocationsGet().subscribe(result => {
      this.metros = result;
    });
  }

  addSectorCheckboxes() {
    this.propertySectors.forEach(() => {
      const control = new FormControl(false);
      (this.estateAgentForm.controls.sectors as FormArray).push(control);
    });
  }

  isOtherSectorChecked(): boolean {
    const sectorsArray = this.estateAgentForm.controls.sectors as FormArray;
    const otherSectorIndex = this.propertySectors.findIndex(sector => sector.value === 'Other');
    return otherSectorIndex !== -1 && sectorsArray.at(otherSectorIndex).value;
  }

  selectButton(buttonName: string): void {
    this.selectedButton = buttonName;
  }

  minSelectedCheckboxes(min = 1) {
    const validator: ValidatorFn = (formArray: FormArray) => {
      const totalSelected = formArray.controls
        .map(control => control.value)
        .reduce((prev, next) => next ? prev + 1 : prev, 0);

      return totalSelected >= min ? null : { required: true };
    };
    return validator;
  }

  async onSubmit() {
    try {
      if (this.estateAgentForm.valid) {
        const selectedSectors = this.estateAgentForm.value.sectors
          .map((checked: boolean, i: number) => checked ? this.propertySectors[i] : null)
          .filter((sector: PropertySectorDto | null) => sector !== null);

        const formValue: EstateAgentPersonalInformationDto = {
          ...this.estateAgentForm.value,
          status: this.estateAgentForm.get('status')?.value,
          sectors: selectedSectors
        };

        let savedMetro = await this.updateUserWithMetroSelected();
        if (savedMetro == true) {
          this._estateAgentRegistrationStateService.updatePersonalInformation(formValue);
          this.pageNumberEvent.emit(1);
          this.formSubmittedEvent.emit();
        } else {
          this.notificationService.showWarningMessage("Warning", "Error when saving estate agent personal details.");
        }
      }
    } catch (err) {
      this.notificationService.showErrorMessage("Error", "Could not save your information. Please contact your administrator.");
    }
  }

  async updateUserWithMetroSelected(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        let metroId = this.estateAgentForm.get('metro')?.value;

        this.previoususerCreated.metro = this.metroList.find(m => m.id == metroId);
        if (this.previoususerCreated.metro && this.previoususerCreated.metro != undefined) {
          this.loading = true;
          this.userManagementService.apiV1UserManagementUpdateMetroToUserPost({ body: this.previoususerCreated })
            .pipe(
              catchError((err) =>
                of(this.notificationService.showErrorMessage("Error", "Could not save metro to current user."))
              ),
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe((result: boolean | void) => {
              if (result) {
                resolve(result);
              } else {
                this.notificationService.showErrorMessage("Error", "Could not save metro selected.");
                resolve(false);
              }
            });
        } else {
          this.notificationService.showWarningMessage("Warning", "The metro is not found and will not update metro to user.")
        }
      } catch (ex) {
        this.notificationService.showErrorMessage("Error", "Could not save metro.");
        this.loading = false;
        resolve(false);
        reject("Error caught on service call save metro to user.");
      }
    });
  }
}
